import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { DataContext } from '../../../context/DataContext';
import { BottomNav } from '../../common/BottomNav';
import { FeedbackLink } from '../../common/FeedbackLink';
import SuggestionCard from './SuggestionCard';
import EncouragementModal from '../../common/EncouragementModal';
import CrossCauseModal from '../../common/CrossCauseModal';

const Container = styled.div`
  background-color: ${theme.colors.background};
  min-height: 100vh;
  padding-bottom: 100px;
`;

const Header = styled.div`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.subtle};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${theme.colors.deepNavy};
  padding: ${theme.spacing.sm};
`;

const Title = styled.h1`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.xlarge};
  color: ${theme.colors.deepNavy};
  margin: 0;
  font-weight: bold;
  flex-grow: 1;
  text-align: center;
`;

const FilterSection = styled.div`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing.md};
  margin: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.subtle};
`;

const FilterTitle = styled.h3`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.deepNavy};
  margin: 0 0 ${theme.spacing.sm} 0;
  font-weight: 600;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const FilterChip = styled.button`
  background-color: ${props => props.active ? theme.colors.brandYellow : theme.colors.white};
  color: ${theme.colors.deepNavy};
  border: 1px solid ${props => props.active ? theme.colors.brandYellow : theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.small};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  &:hover {
    background-color: ${props => props.active ? theme.colors.brandYellow : theme.colors.iceBlue};
    border-color: ${theme.colors.brandYellow};
  }
`;

const FilterIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.slateGray};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.small};
  cursor: pointer;
  text-decoration: underline;
  padding: ${theme.spacing.xs} 0;
  margin-top: ${theme.spacing.sm};

  &:hover {
    color: ${theme.colors.deepNavy};
  }
`;

const ResultsSection = styled.div`
  padding: ${theme.spacing.md};
`;

const ResultCount = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
  margin-bottom: ${theme.spacing.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.slateGray};
`;

const GetInspiredScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { causes, addCause } = useContext(DataContext);

  const [suggestions, setSuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedCauses, setSelectedCauses] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);

  // Modal states
  const [showEncouragementModal, setShowEncouragementModal] = useState(false);
  const [showCrossCauseModal, setShowCrossCauseModal] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [userLevelForSuggestion, setUserLevelForSuggestion] = useState(null);

  // Load suggestions from JSON
  useEffect(() => {
    const loadSuggestions = async () => {
      try {
        const response = await fetch('/data/suggestions.json');
        const data = await response.json();
        setSuggestions(data.activities);
        setFilteredSuggestions(data.activities);
      } catch (error) {
        console.error('Error loading suggestions:', error);
      }
    };
    loadSuggestions();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = suggestions;

    // Filter by causes
    if (selectedCauses.length > 0) {
      filtered = filtered.filter(s =>
        selectedCauses.includes(s.cause) || s.cause === 'any'
      );
    }

    // Filter by levels
    if (selectedLevels.length > 0) {
      filtered = filtered.filter(s =>
        selectedLevels.includes(s.level)
      );
    }

    setFilteredSuggestions(filtered);
  }, [selectedCauses, selectedLevels, suggestions]);

  // Toggle filter selection
  const toggleFilter = (filterArray, setFilterArray, value) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter(v => v !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCauses([]);
    setSelectedLevels([]);
  };

  // Handle "Try This Activity" click
  const handleTryActivity = (suggestion) => {
    setPendingSuggestion(suggestion);

    // Check if this cause is already in user's causes
    const userHasCause = causes.some(c => c.id === suggestion.cause);

    if (!userHasCause && suggestion.cause !== 'any') {
      // Show cross-cause modal
      setShowCrossCauseModal(true);
      return;
    }

    // Get user's level for this cause
    const userCause = causes.find(c => c.id === suggestion.cause);
    const userLevel = userCause?.level?.title || 'Streetlight';

    // Check if we need encouragement modal
    const needsEncouragement = shouldShowEncouragement(userLevel, suggestion.level);

    if (needsEncouragement) {
      setUserLevelForSuggestion(userLevel);
      setShowEncouragementModal(true);
    } else {
      // Go directly to log activity
      navigateToLogActivity(suggestion);
    }
  };

  // Determine if encouragement modal is needed
  const shouldShowEncouragement = (userLevel, suggestionLevel) => {
    const levelNumbers = {
      'Streetlight': 1,
      'Daylight': 2,
      'Sunshield': 3
    };

    const userLevelNum = levelNumbers[userLevel] || 1;
    const suggestionLevelNum = levelNumbers[suggestionLevel] || 1;

    // Show encouragement if user is stepping UP
    return suggestionLevelNum > userLevelNum;
  };

  // Navigate to Log Activity screen with pre-filled data
  const navigateToLogActivity = (suggestion) => {
    navigate('/log-activity', {
      state: {
        prefilledActivity: suggestion.title,
        prefilledCause: suggestion.cause,
        fromInspiration: true
      }
    });
  };

  // Handle encouragement modal - user proceeds
  const handleEncouragementProceed = () => {
    setShowEncouragementModal(false);
    navigateToLogActivity(pendingSuggestion);
    setPendingSuggestion(null);
    setUserLevelForSuggestion(null);
  };

  // Handle encouragement modal - user goes back
  const handleEncouragementGoBack = () => {
    setShowEncouragementModal(false);
    setPendingSuggestion(null);
    setUserLevelForSuggestion(null);
  };

  // Handle cross-cause modal - add cause and log
  const handleAddCauseAndLog = async () => {
    try {
      // Find the cause data
      const causeMapping = {
        'homelessness': {
          id: 'homelessness',
          title: 'Homelessness Awareness',
          description: 'Track outreach, resource sharing, and policy advocacy to address homelessness.',
          icon: '/images/cause_homeless_awareness.png'
        },
        'beautification': {
          id: 'beautification',
          title: 'Community Beautification',
          description: 'Participate in cleanup efforts, public space improvements, and local projects.',
          icon: '/images/cause_comunity_beautification.png'
        },
        'trafficking': {
          id: 'trafficking',
          title: 'Human Trafficking Awareness',
          description: 'Combat trafficking through education, reporting mechanisms, and support.',
          icon: '/images/cause_human_traffic.png'
        }
      };

      const causeData = causeMapping[pendingSuggestion.cause];

      if (causeData) {
        // Add default level (Streetlight)
        const causeWithLevel = {
          ...causeData,
          level: {
            id: 1,
            title: 'Streetlight',
            description: "I'm just getting started or prefer small, personal acts of advocacy."
          }
        };

        await addCause(causeWithLevel);
      }

      setShowCrossCauseModal(false);
      navigateToLogActivity(pendingSuggestion);
      setPendingSuggestion(null);
    } catch (error) {
      console.error('Error adding cause:', error);
      alert('Failed to add cause. Please try again.');
    }
  };

  // Handle cross-cause modal - just log activity
  const handleJustLog = () => {
    setShowCrossCauseModal(false);
    navigateToLogActivity(pendingSuggestion);
    setPendingSuggestion(null);
  };

  // Handle cross-cause modal - go back
  const handleCrossCauseGoBack = () => {
    setShowCrossCauseModal(false);
    setPendingSuggestion(null);
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/home')}>←</BackButton>
        <Title>{t('inspired.title')}</Title>
        <div style={{ width: '40px' }} /> {/* Spacer for centering */}
      </Header>

      <FilterSection>
        <FilterTitle>{t('inspired.filterByCause')}</FilterTitle>
        <FilterOptions>
          <FilterChip
            active={selectedCauses.includes('homelessness')}
            onClick={() => toggleFilter(selectedCauses, setSelectedCauses, 'homelessness')}
          >
            Homelessness
          </FilterChip>
          <FilterChip
            active={selectedCauses.includes('beautification')}
            onClick={() => toggleFilter(selectedCauses, setSelectedCauses, 'beautification')}
          >
            Beautification
          </FilterChip>
          <FilterChip
            active={selectedCauses.includes('trafficking')}
            onClick={() => toggleFilter(selectedCauses, setSelectedCauses, 'trafficking')}
          >
            Trafficking
          </FilterChip>
        </FilterOptions>

        <FilterTitle>{t('inspired.filterByLevel')}</FilterTitle>
        <FilterOptions>
          <FilterChip
            active={selectedLevels.includes('Streetlight')}
            onClick={() => toggleFilter(selectedLevels, setSelectedLevels, 'Streetlight')}
          >
            <FilterIcon src="/icons/icon_streetlight.png" alt="Streetlight" />
            Streetlight
          </FilterChip>
          <FilterChip
            active={selectedLevels.includes('Daylight')}
            onClick={() => toggleFilter(selectedLevels, setSelectedLevels, 'Daylight')}
          >
            <FilterIcon src="/icons/icon_daylight.png" alt="Daylight" />
            Daylight
          </FilterChip>
          <FilterChip
            active={selectedLevels.includes('Sunshield')}
            onClick={() => toggleFilter(selectedLevels, setSelectedLevels, 'Sunshield')}
          >
            <FilterIcon src="/icons/icon_sunshield.png" alt="Sunshield" />
            Sunshield
          </FilterChip>
        </FilterOptions>

        {(selectedCauses.length > 0 || selectedLevels.length > 0) && (
          <ClearButton onClick={clearFilters}>
            {t('inspired.clearFilters')}
          </ClearButton>
        )}
      </FilterSection>

      <ResultsSection>
        <ResultCount>
          {filteredSuggestions.length === 1 ? t('inspired.activityFound', { count: filteredSuggestions.length }) : t('inspired.activitiesFound', { count: filteredSuggestions.length })}
        </ResultCount>

        {filteredSuggestions.length === 0 ? (
          <EmptyState>
            <p>{t('inspired.noResults')}</p>
            <p>{t('inspired.noResultsHint')}</p>
          </EmptyState>
        ) : (
          filteredSuggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              onTryActivity={handleTryActivity}
            />
          ))
        )}
      </ResultsSection>

      <FeedbackLink />
      <BottomNav />

      {/* Encouragement Modal */}
      {showEncouragementModal && (
        <EncouragementModal
          userLevel={userLevelForSuggestion}
          suggestionLevel={pendingSuggestion?.level}
          onProceed={handleEncouragementProceed}
          onGoBack={handleEncouragementGoBack}
        />
      )}

      {/* Cross-Cause Modal */}
      {showCrossCauseModal && (
        <CrossCauseModal
          causeName={pendingSuggestion?.causeName}
          onAddCause={handleAddCauseAndLog}
          onJustLog={handleJustLog}
          onGoBack={handleCrossCauseGoBack}
        />
      )}
    </Container>
  );
};

export default GetInspiredScreen;
